/**
 * `IIdentityProofingClient` — interface for the SHT-mediated
 * identity-proofing vendor handoff.
 *
 * The PHG client hands the patient over to an SHC-approved identity-
 * proofing vendor for NIST IAL2 identity verification per PFO §2.2
 * during account activation. This module defines the consumer-side
 * contract; the vendor-integration choice (Stripe Identity, Persona,
 * Onfido, Jumio, equivalent — TBD by PBC operational team under
 * vendor-selection / procurement, hire-gated per the DEV5 onboarding
 * handoff §"You do NOT own") is decoupled from the client-side flow.
 *
 * SCAFFOLD: a `MockIdentityProofingClient` satisfies the contract
 * for tests and dev environments without making network calls; the
 * production wiring lands when PBC operational team selects the
 * vendor and the senior-infrastructure / DevOps engineer hire wires
 * the procurement-gated production integration.
 *
 * **Doctrinal posture.** Identity proofing collects identity
 * documents, government-issued ID images, and biometric data. Per
 * Tech Spec §6.2.3 metadata-channel disclosure, this material
 * passes through SHT-mediated vendor surfaces and is governed by
 * the vendor's certified discipline + SHT-set retention policy.
 * The web client does NOT retain identity-proofing artifacts in
 * patient session memory beyond what's required to surface the
 * vendor's verdict to the patient.
 */
// ---------- Mock implementation (test + dev) ----------
/**
 * Scriptable mock for tests and dev environments. Tests configure
 * the desired outcome via `seedNextOutcome`; the dev server uses the
 * default `'verified'` outcome with a synthetic attestation token.
 */
export class MockIdentityProofingClient {
    counter = 0;
    nextOutcome = 'verified';
    nextAssuranceLevel = 'IAL2';
    nextAccommodation = undefined;
    resolved = new Map();
    async openSession(opts) {
        const sessionId = `idp_mock_${++this.counter}`;
        const result = {
            sessionId,
            outcome: this.nextOutcome,
            assuranceLevel: this.nextAssuranceLevel,
            attestationToken: `attest_mock_${sessionId}`,
            ...(this.nextAccommodation !== undefined
                ? { accommodationKind: this.nextAccommodation }
                : {}),
        };
        this.resolved.set(sessionId, result);
        return {
            sessionId,
            vendorRedirectUrl: `https://example.invalid/idp-mock/${sessionId}?locale=${encodeURIComponent(opts.locale)}`,
            openedAt: new Date().toISOString(),
        };
    }
    async resolveSession(sessionId) {
        const r = this.resolved.get(sessionId);
        if (r === undefined) {
            throw new Error(`mock identity-proofing session not found: ${sessionId}`);
        }
        return r;
    }
    /** Test-double helper: configure the next session's outcome. */
    seedNextOutcome(opts) {
        this.nextOutcome = opts.outcome;
        this.nextAssuranceLevel = opts.assuranceLevel ?? 'IAL2';
        this.nextAccommodation = opts.accommodation;
    }
}
//# sourceMappingURL=identity-proofing-client.js.map